import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import useBarryViewState from 'components/Barry/hooks/useBarryController '
import { useLocation } from '@reach/router'
import { differenceInMonths } from 'date-fns'
import { useGetHelloBarryMessageQuery } from 'store/api/support/support'
import { BarryPopUpMessagAction } from 'components/Barry/BarryComponents/BarryPopUp'

export default function useBarryStaticPage(): {
  handleStaticPageDialogToggle: () => void
  handleHelloMessagePopupInteraction: (action: BarryPopUpMessagAction) => void
  hasHelloMessagePopup: boolean
  barryHelloMessage?: { cta?: string }
} {
  const { isBarryDialogOpen, dismissHelloPopupMessage, setBarryDialogOpen } = useBarryViewState()
  const { hasHelloMessagePopup, popupMessage } = useHelloPopupMessageState()

  const handleStaticPageDialogToggle = useCallback(() => {
    if (!isBarryDialogOpen && hasHelloMessagePopup) {
      dismissHelloPopupMessage()
    }
    setBarryDialogOpen(!isBarryDialogOpen)
  }, [isBarryDialogOpen, hasHelloMessagePopup, dismissHelloPopupMessage, setBarryDialogOpen])

  const handleHelloMessagePopupInteraction = useCallback(
    (action: BarryPopUpMessagAction) => {
      dismissHelloPopupMessage()
      if (action === BarryPopUpMessagAction.OPEN) {
        //Clicking text on hello popup should open the window
        setBarryDialogOpen(true)
      } else {
        setBarryDialogOpen(false)
      }
    },
    [dismissHelloPopupMessage, setBarryDialogOpen],
  )

  return {
    handleStaticPageDialogToggle,
    handleHelloMessagePopupInteraction,
    hasHelloMessagePopup,
    barryHelloMessage: popupMessage,
  }
}

function useHelloPopupMessageState(): {
  hasHelloMessagePopup: boolean
  popupMessage?: { cta?: string }
} {
  const location = useLocation()
  const pathname = location.pathname
  const [isHelloPopupMessageReady, setIsHelloPopupMessageReady] = useState(false)
  const { hasHelloPopupBeenDismissed } = useAppSelector(s => s.barry.userExperience)

  const shouldFetchHelloPopupMessage = hasHelloPopupBeenDismissed
    ? differenceInMonths(new Date(), new Date(hasHelloPopupBeenDismissed)) >= 1
    : true

  const { data: barryHelloMessage, isSuccess: isBarryHelloMessageSuccess } =
    useGetHelloBarryMessageQuery({ on_page: pathname }, { skip: !shouldFetchHelloPopupMessage })
  const isHelloMessageAvailable = !!barryHelloMessage && isBarryHelloMessageSuccess

  const hasHelloMessagePopup =
    isHelloPopupMessageReady && isHelloMessageAvailable && hasHelloPopupBeenDismissed === undefined

  const timeoutId = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }

    if (isBarryHelloMessageSuccess) {
      setIsHelloPopupMessageReady(false)
      timeoutId.current = setTimeout(() => {
        setIsHelloPopupMessageReady(true)
      }, 3000)
    }

    return () => clearTimeout(timeoutId.current)
  }, [isBarryHelloMessageSuccess, setIsHelloPopupMessageReady])

  return { hasHelloMessagePopup, popupMessage: barryHelloMessage }
}
